import React from 'react';
import { Player } from '../../../types';
import { Tooltip } from 'react-tooltip';

interface PlayerReviewsProps {
  player: Player;
  currentUserId?: string;
  isMobile: boolean;
  isCompact: boolean;
  onCreateReview?: (player: Player) => void;
  onViewReviews?: (player: Player) => void;
  hasReviewedPlayer?: boolean;
  canViewReviews?: boolean;
  loadingReviews?: boolean;
}

export const PlayerReviews: React.FC<PlayerReviewsProps> = ({
  player,
  currentUserId,
  isMobile,
  isCompact,
  onCreateReview,
  onViewReviews,
  hasReviewedPlayer,
  canViewReviews,
  loadingReviews
}) => {
  const isDesktop = !isMobile && !isCompact;
  const canCreateReview = currentUserId && player.userId && player.userId !== currentUserId && onCreateReview;
  const canView = player.userId && onViewReviews && currentUserId;

  // Si no hay usuario autenticado, mostrar mensaje de registro
  if (!currentUserId) {
    return (
      <button type="button" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: isDesktop ? '6px 12px' : '8px 12px',
        backgroundColor: '#374151',
        borderRadius: isDesktop ? '6px' : '8px',
        border: '1px solid #4B5563',
        color: '#9CA3AF',
        fontSize: isDesktop ? '11px' : '12px',
        fontWeight: '500',
        fontFamily: 'inherit',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        width: 'fit-content'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#4B5563';
        e.currentTarget.style.color = '#D1D5DB';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = '#374151';
        e.currentTarget.style.color = '#9CA3AF';
      }}
      onClick={() => {
        window.location.href = '/auth';
      }}
      >
        🔐 Regístrate para dar y recibir reviews
      </button>
    );
  }

  // Botones para desktop
  if (isDesktop) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        marginLeft: '8px',
        padding: '2px 4px',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '4px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        flexShrink: 0
      }}>
        {/* Botón crear review */}
        {canCreateReview && (
          <button
            data-tooltip-id={`review-tooltip-${player.id}`}
            data-tooltip-content={
              loadingReviews ? "Cargando reviews..." : 
              hasReviewedPlayer ? "Ya le hiciste review a este jugador" : 
              "📝 Dejar review para este jugador"
            }
            onClick={(e) => {
              e.stopPropagation();
              if (!hasReviewedPlayer && !loadingReviews) {
                onCreateReview(player);
              }
            }}
            disabled={hasReviewedPlayer || loadingReviews}
            style={{
              background: hasReviewedPlayer ? '#666666' : loadingReviews ? '#999999' : '#FF6B35',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 10px',
              color: 'white',
              cursor: (hasReviewedPlayer || loadingReviews) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 'bold',
              fontFamily: 'inherit',
              transition: 'background-color 0.2s',
              opacity: (hasReviewedPlayer || loadingReviews) ? 0.6 : 1,
              minWidth: 'auto',
              minHeight: 'auto',
              gap: '4px'
            }}
            onMouseEnter={(e) => {
              if (!hasReviewedPlayer && !loadingReviews) {
                e.currentTarget.style.backgroundColor = '#E55A2B';
              }
            }}
            onMouseLeave={(e) => {
              if (!hasReviewedPlayer && !loadingReviews) {
                e.currentTarget.style.backgroundColor = '#FF6B35';
              }
            }}
          >
            {loadingReviews ? '⏳' : '📝 Review'}
          </button>
        )}
        
        {/* Botón ver reviews */}
        {canView && (
          <button
            data-tooltip-id={!canViewReviews ? `reviews-tooltip-${player.id}` : undefined}
            data-tooltip-content={!canViewReviews ? "✨ Dejá tu reseña y descubrí qué dijeron de vos" : "📋 Ver reviews de este jugador"}
            onClick={(e) => {
              e.stopPropagation();
              if (canViewReviews) {
                onViewReviews(player);
              }
            }}
            disabled={!canViewReviews}
            style={{
              background: canViewReviews ? '#00E676' : '#666666',
              border: 'none',
              borderRadius: '6px',
              padding: '6px 10px',
              color: 'white',
              cursor: canViewReviews ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 'bold',
              fontFamily: 'inherit',
              transition: 'background-color 0.2s',
              opacity: canViewReviews ? 1 : 0.6,
              minWidth: 'auto',
              minHeight: 'auto',
              gap: '4px'
            }}
            onMouseEnter={(e) => {
              if (canViewReviews) {
                e.currentTarget.style.backgroundColor = '#00C853';
              }
            }}
            onMouseLeave={(e) => {
              if (canViewReviews) {
                e.currentTarget.style.backgroundColor = '#00E676';
              }
            }}
          >
            📋 Ver Reviews
          </button>
        )}
      </div>
    );
  }

  // Botones para mobile/compact
  return (
    <div style={{
      marginTop: '8px',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      alignItems: 'flex-start'
    }}>
      {/* Botón crear review */}
      {canCreateReview && (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            data-tooltip-id={`review-tooltip-${player.id}`}
            data-tooltip-content={
              loadingReviews ? "Cargando reviews..." : 
              hasReviewedPlayer ? "Ya le hiciste review a este jugador" : 
              "📝 Dejar review para este jugador"
            }
            onClick={(e) => {
              e.stopPropagation();
              if (!hasReviewedPlayer && !loadingReviews) {
                onCreateReview(player);
              }
            }}
            disabled={hasReviewedPlayer || loadingReviews}
            style={{
              background: hasReviewedPlayer ? '#666666' : loadingReviews ? '#999999' : '#FF6B35',
              border: 'none',
              borderRadius: '8px',
              padding: '4px 8px',
              color: 'white',
              cursor: (hasReviewedPlayer || loadingReviews) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              flexShrink: 0,
              fontFamily: 'inherit',
              transition: 'background-color 0.2s',
              opacity: (hasReviewedPlayer || loadingReviews) ? 0.6 : 1
            }}
            onMouseEnter={(e) => {
              if (!hasReviewedPlayer && !loadingReviews) {
                e.currentTarget.style.backgroundColor = '#E55A2B';
              }
            }}
            onMouseLeave={(e) => {
              if (!hasReviewedPlayer && !loadingReviews) {
                e.currentTarget.style.backgroundColor = '#FF6B35';
              }
            }}
          >
            {loadingReviews ? '⏳ Cargando...' : '📝 Dejar Review'}
          </button>
          <Tooltip 
            id={`review-tooltip-${player.id}`}
            style={{
              backgroundColor: hasReviewedPlayer ? '#FF6B6B' : '#FF6B35',
              color: '#FFFFFF',
              fontSize: '11px',
              fontWeight: 'bold',
              borderRadius: '6px',
              padding: '6px 10px',
              zIndex: 1000
            }}
          />
        </div>
      )}
      
      {/* Botón ver reviews */}
      {canView && (
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <button
            data-tooltip-id={!canViewReviews ? `reviews-tooltip-${player.id}` : undefined}
            data-tooltip-content={!canViewReviews ? "✨ Dejá tu reseña y descubrí qué dijeron de vos" : "📋 Ver reviews de este jugador"}
            onClick={(e) => {
              e.stopPropagation();
              if (canViewReviews) {
                onViewReviews(player);
              }
            }}
            disabled={!canViewReviews}
            style={{
              background: canViewReviews ? '#00E676' : '#666666',
              border: 'none',
              borderRadius: '8px',
              padding: '4px 8px',
              color: 'white',
              cursor: canViewReviews ? 'pointer' : 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: 'bold',
              flexShrink: 0,
              fontFamily: 'inherit',
              transition: 'background-color 0.2s',
              opacity: canViewReviews ? 1 : 0.6
            }}
            onMouseEnter={(e) => {
              if (canViewReviews) {
                e.currentTarget.style.backgroundColor = '#00C853';
              }
            }}
            onMouseLeave={(e) => {
              if (canViewReviews) {
                e.currentTarget.style.backgroundColor = '#00E676';
              }
            }}
          >
            📋 Ver Reviews
          </button>
          {!canViewReviews && (
            <Tooltip 
              id={`reviews-tooltip-${player.id}`}
              style={{
                backgroundColor: '#00C853',
                color: '#FFFFFF',
                fontSize: '11px',
                fontWeight: 'bold',
                borderRadius: '6px',
                padding: '6px 10px',
                zIndex: 1000
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};
